/**
 * Hand-off of a just-captured meal photo from /log to /meal/:id.
 *
 * The meal page needs the base64 for the fast analysis path before the
 * background upload finishes. Primary store is in-memory (never fails, never
 * outlives the tab); sessionStorage is a best-effort fallback so a reload
 * between the two pages still works. Entries are removed once the uploaded
 * photo URL exists, when the meal is deleted, and on sign-out.
 */
export interface StashedPhoto {
  base64: string
  /** data: URL — stays valid across reloads, unlike a blob: URL. */
  url: string
}

const KEY = (mealId: string) => `gsnap_local_${mealId}`
const memory = new Map<string, StashedPhoto>()

export function setPhotoStash(mealId: string, photo: StashedPhoto): void {
  memory.set(mealId, photo)
  try {
    sessionStorage.setItem(KEY(mealId), JSON.stringify(photo))
  } catch {
    /* quota / private mode — the in-memory copy still serves this tab */
  }
}

export function getPhotoStash(mealId: string): StashedPhoto | null {
  const m = memory.get(mealId)
  if (m) return m
  try {
    const raw = sessionStorage.getItem(KEY(mealId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StashedPhoto
    if (parsed && typeof parsed.base64 === 'string') {
      // Older entries stored a blob: URL; rebuild a durable data: URL.
      if (!parsed.url?.startsWith('data:')) parsed.url = `data:image/jpeg;base64,${parsed.base64}`
      memory.set(mealId, parsed)
      return parsed
    }
  } catch {
    /* ignore */
  }
  return null
}

export function clearPhotoStash(mealId: string): void {
  memory.delete(mealId)
  try {
    sessionStorage.removeItem(KEY(mealId))
  } catch {
    /* ignore */
  }
}

export function clearAllPhotoStash(): void {
  memory.clear()
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i)
      if (k && k.startsWith('gsnap_local_')) sessionStorage.removeItem(k)
    }
  } catch {
    /* ignore */
  }
}
